import {
    APP_SHELL_TOP_OFFSET_SX,
    MAIN_SECTION_MIN_HEIGHT_SX,
    MOBILE_NAV_MEDIA,
} from "../layout/layoutConstants";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import { DIALOG_ACCEPT_BUTTON_SX } from "../common/dialogActionButtonSx";

const SETTINGS_SIDEBAR_STICKY_TOP = APP_SHELL_TOP_OFFSET_SX;

const GOLD_GLOW = "rgba(232, 163, 61, 0.22)";

export const SETTINGS_SIDEBAR_WIDTH_PX = 300;
export const SETTINGS_SIDEBAR_PAD_X = "1.85rem";
export const SETTINGS_SIDEBAR_PAD_X_MOBILE = "0.7rem";
export const SETTINGS_SIDEBAR_NAV_INSET = "1.35rem";
export const SETTINGS_SIDEBAR_NAV_INSET_MOBILE = "0.9rem";
export const ACCOUNT_PAGE_MOBILE_MQ = `@media ${MOBILE_NAV_MEDIA}`;

export const ACCOUNT_PAGE_CARD_SX = {
    position: "relative",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    padding: 0,
    overflow: "hidden",
    borderRadius: "6px",
    background: `
        linear-gradient(
            180deg,
            rgba(10,10,12,0.96) 0%,
            rgba(6,6,8,0.98) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.05)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    boxShadow: `
        0 20px 40px rgba(0,0,0,0.45),
        inset 0 1px 0 rgba(255,255,255,0.04)
    `,
    transform: "translateZ(0)",
    isolation: "isolate",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "7rem",
        background: `
            radial-gradient(
                ellipse 70% 100% at top,
                rgba(232,163,61,0.05),
                transparent 70%
            )
        `,
        pointerEvents: "none",
    },
    "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "12%",
        right: "12%",
        height: "1px",
        background: `
            linear-gradient(
                90deg,
                transparent,
                rgba(255,255,255,0.18),
                transparent
            )
        `,
        pointerEvents: "none",
    },
};

export const ACCOUNT_PAGE_CARD_NOISE_SX = {
    position: "absolute",
    inset: 0,
    opacity: 0.035,
    pointerEvents: "none",
    backgroundImage: `
        radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
        radial-gradient(circle at 75% 75%, white 1px, transparent 1px)
    `,
    backgroundSize: "120px 120px",
};

export const ACCOUNT_PAGE_CARD_CONTENT_SX = {
    position: "relative",
    zIndex: 1,
    width: "100%",
    minWidth: 0,
    flex: 1,
};

export const ACCOUNT_PAGE_CONTENT_PAD_Y = "2.35rem";

export const ACCOUNT_PAGE_MAIN_CONTENT_SX = {
    ...ACCOUNT_PAGE_CARD_CONTENT_SX,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    px: "1.5rem",
    pt: ACCOUNT_PAGE_CONTENT_PAD_Y,
    pb: "3.25rem",
    boxSizing: "border-box",
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        pt: "1.15rem",
        px: "1.15rem",
    },
};

export const ACCOUNT_PAGE_SECTION_SX = {
    width: "100%",
    maxWidth: "42rem",
    mx: "auto",
    minWidth: 0,
};

export const ACCOUNT_PAGE_SECTION_HEADER_SX = {
    paddingBottom: "1.75rem",
    marginBottom: "0.4rem",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
};

export const ACCOUNT_PAGE_OVERVIEW_STACK_SX = {
    display: "flex",
    flexDirection: "column",
    gap: "1.25rem",
    width: "100%",
    mt: "1.75rem",
    mb: "1.75rem",
};

export const ACCOUNT_PAGE_REVIEW_NOTICE_SX = {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    width: "100%",
    px: { xs: "0.9rem", sm: "1rem" },
    py: { xs: "0.8rem", sm: "0.9rem" },
    boxSizing: "border-box",
    borderRadius: "10px",
    border: `1px solid ${alpha(colors.primary.main, 0.3)}`,
    background: `linear-gradient(90deg, ${alpha(colors.primary.main, 0.12)} 0%, ${alpha(colors.background.surface, 0.96)} 58%)`,
    boxShadow: `inset 0 1px 0 ${alpha(colors.text.primary, 0.035)}`,
    color: colors.text.primary,
    "& .account-review-notice-icon": {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: "2rem",
        height: "2rem",
        borderRadius: "8px",
        backgroundColor: alpha(colors.primary.main, 0.14),
        color: colors.primary.main,
    },
    "& .account-review-notice-icon svg": {
        fontSize: "1.1rem",
    },
    "& .account-review-notice-text": {
        minWidth: 0,
        color: colors.text.primary,
        fontSize: { xs: "0.78rem", sm: "0.84rem" },
        fontWeight: 650,
        lineHeight: 1.4,
        letterSpacing: "0.02em",
    },
};

export const ACCOUNT_PAGE_READINESS_SX = {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    width: "100%",
    px: { xs: "1rem", sm: "1.35rem" },
    py: { xs: "1.15rem", sm: "1.3rem" },
    borderRadius: "6px",
    border: `1px solid ${alpha(colors.primary.main, 0.24)}`,
    background: `
        linear-gradient(
            135deg,
            rgba(26,20,12,0.98) 0%,
            rgba(15,14,16,0.98) 54%,
            rgba(10,10,12,0.99) 100%
        )
    `,
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.04),
        0 12px 30px rgba(0,0,0,0.18)
    `,
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "10%",
        right: "10%",
        height: "1px",
        background: `linear-gradient(90deg, transparent, ${alpha(colors.primary.main, 0.35)}, transparent)`,
        pointerEvents: "none",
    },
    position: "relative",
    overflow: "hidden",
};

export const ACCOUNT_PAGE_READINESS_COUNT_SX = {
    color: colors.text.primary,
    fontSize: "1.3rem",
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "0.02em",
    lineHeight: 1,
    flexShrink: 0,
};

export const ACCOUNT_PAGE_READINESS_HEADER_SX = {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.8rem",
    width: "100%",
};

export const ACCOUNT_PAGE_READINESS_HEADER_ICON_SX = {
    display: "grid",
    placeItems: "center",
    width: "2.35rem",
    height: "2.35rem",
    flexShrink: 0,
    borderRadius: "9px",
    color: colors.accent.soft,
    background: alpha(colors.primary.main, 0.14),
    border: `1px solid ${alpha(colors.primary.main, 0.28)}`,
    boxShadow: `inset 0 1px 0 ${alpha(colors.accent.soft, 0.18)}`,
    "& svg": { fontSize: "1.25rem" },
};

export const ACCOUNT_PAGE_READINESS_KICKER_SX = {
    color: colors.accent.soft,
    fontSize: "0.66rem",
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: "0.11em",
    textTransform: "uppercase",
};

export const ACCOUNT_PAGE_READINESS_INTRO_SX = {
    mt: "0.25rem",
    color: colors.text.secondary,
    fontSize: "0.78rem",
    fontWeight: 500,
    lineHeight: 1.4,
};

export const ACCOUNT_PAGE_READINESS_PROGRESS_TRACK_SX = {
    display: "flex",
    gap: "3px",
    width: "100%",
    height: "5px",
    padding: "1px",
    borderRadius: "999px",
    background: "rgba(255,255,255,0.06)",
};

export const ACCOUNT_PAGE_READINESS_PROGRESS_SEGMENT_SX = {
    flex: 1,
    minWidth: 0,
    clipPath: "polygon(2px 0, 100% 0, calc(100% - 2px) 100%, 0 100%)",
};

export const ACCOUNT_PAGE_READINESS_CHECKS_SX = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
    gap: "0.55rem",
    width: "100%",
};

export const ACCOUNT_PAGE_READINESS_CHECK_SX = {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.55rem",
    minWidth: 0,
    px: "0.7rem",
    py: "0.65rem",
    borderRadius: "9px",
    border: "1px solid",
    boxSizing: "border-box",
};

export const ACCOUNT_PAGE_SUMMARY_SX = {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "center" },
    gap: { xs: "1.1rem", sm: "1.35rem" },
    width: "100%",
    px: "1.15rem",
    py: "1.1rem",
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: `
        linear-gradient(
            165deg,
            rgba(18,18,22,0.92) 0%,
            rgba(12,12,14,0.96) 52%,
            rgba(10,10,12,0.98) 100%
        )
    `,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
};

export const ACCOUNT_PAGE_SUMMARY_COLUMN_SX = {
    display: "flex",
    flexDirection: "column",
    gap: "0.55rem",
    minWidth: 0,
    flex: { sm: "0 0 auto" },
};

export const ACCOUNT_PAGE_SUMMARY_DIVIDER_SX = {
    display: { xs: "none", sm: "block" },
    alignSelf: "stretch",
    width: "1px",
    flexShrink: 0,
    background: "rgba(255,255,255,0.06)",
};

export const ACCOUNT_PAGE_SUMMARY_STATUS_CHIP_SX = {
    display: "inline-flex",
    alignItems: "center",
    alignSelf: "flex-start",
    px: "0.75rem",
    py: "0.38rem",
    borderRadius: "999px",
    border: "1px solid",
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    lineHeight: 1.2,
    textTransform: "none",
    whiteSpace: "nowrap",
};

export const ACCOUNT_PAGE_SUMMARY_SCORE_SX = {
    color: colors.text.primary,
    fontSize: "0.95rem",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "0.02em",
    lineHeight: 1,
    flexShrink: 0,
};

export const ACCOUNT_PAGE_SUMMARY_PROGRESS_TRACK_SX = {
    display: "flex",
    gap: "2px",
    width: "100%",
    height: "3px",
};

export const ACCOUNT_PAGE_SUMMARY_PROGRESS_SEGMENT_SX = {
    flex: 1,
    minWidth: 0,
    clipPath: "polygon(2px 0, 100% 0, calc(100% - 2px) 100%, 0 100%)",
};

export const ACCOUNT_PAGE_SUBSECTION_SX = {
    mt: "1.5rem",
};

export const ACCOUNT_PAGE_SUBSECTION_DIVIDER_SX = {
    width: "100%",
    mt: "1.5rem",
    mb: "1.5rem",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
};

export const ACCOUNT_PAGE_SUBSECTION_TITLE_SX = {
    color: colors.text.primary,
    fontSize: { xs: "1.15rem", sm: "1.375rem" },
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: "0.01em",
};

export const ACCOUNT_PAGE_TITLE_SX = {
    color: colors.text.primary,
    fontSize: { xs: "1.45rem", sm: "1.75rem" },
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: "0.01em",
};

export const ACCOUNT_PAGE_SUBSECTION_TOGGLE_SX = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.75rem",
    width: "100%",
    margin: 0,
    padding: "0.15rem 0",
    border: "none",
    outline: "none",
    background: "transparent",
    color: "inherit",
    font: "inherit",
    cursor: "pointer",
    textAlign: "left",
    userSelect: "none",
    "&:focus, &:focus-visible": {
        outline: "none",
    },
};

export const ACCOUNT_PAGE_SUBSECTION_ARROW_SX = {
    color: colors.text.primary,
    fontSize: { xs: "1.5rem", sm: "1.75rem" },
    flexShrink: 0,
    transition: "opacity 0.15s ease-in-out",
};

export const ACCOUNT_PAGE_FIELD_LABEL_SX = {
    color: colors.text.secondary,
    fontSize: "0.875rem",
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: "0.04em",
};

export const ACCOUNT_PAGE_FIELD_DIVIDER_SX = {
    width: "100%",
    mt: "1.5rem",
    mb: "1.25rem",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
};

export const ACCOUNT_PAGE_FIELD_ROW_SX = {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    width: "100%",
};

export const ACCOUNT_PAGE_FIELD_INPUT_WRAP_SX = {
    width: "100%",
    maxWidth: "18rem",
    minWidth: 0,
};

export const ACCOUNT_PAGE_FIELD_INPUT_WRAP_WIDE_SX = {
    width: "100%",
    maxWidth: "36.75rem",
    minWidth: 0,
};

export const ACCOUNT_PAGE_FIELD_EDIT_BUTTON_SX = {
    ml: "auto",
    flexShrink: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    color: colors.primary.main,
    font: "inherit",
    fontSize: "0.85rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textDecoration: "none",
    cursor: "pointer",
    padding: "0.4rem 0.65rem",
    borderRadius: "4px",
    transition: "background 0.15s ease-in-out, color 0.15s ease-in-out",
    "&:hover": {
        color: colors.primary.main,
        background: colors.accent.subtle,
        textDecoration: "none",
    },
    "&:focus, &:focus-visible": {
        outline: "none",
    },
};

export const ACCOUNT_PAGE_FIELD_ACTIONS_SX = {
    display: "flex",
    alignItems: "center",
    gap: "0.55rem",
    width: "100%",
    maxWidth: "18rem",
    mt: "0.55rem",
};

export const ACCOUNT_PAGE_FIELD_SAVE_BUTTON_SX = {
    flexShrink: 0,
    minWidth: "5.75rem",
    height: "2.15rem",
    px: "1.35rem",
    border: "none",
    outline: "none",
    borderRadius: "6px",
    background: colors.primary.hover,
    color: colors.text.contrast,
    font: "inherit",
    fontSize: "0.78rem",
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    cursor: "pointer",
    boxShadow: "none",
    transition: "background 0.15s ease-in-out, color 0.15s ease-in-out",
    "&:hover": {
        background: colors.primary.active,
    },
    "&:focus, &:focus-visible": {
        outline: "none",
    },
};

export const ACCOUNT_PAGE_FIELD_CANCEL_BUTTON_SX = {
    flexShrink: 0,
    minWidth: "5.75rem",
    height: "2.15rem",
    px: "1.35rem",
    border: "none",
    outline: "none",
    borderRadius: "6px",
    background: "rgba(255,255,255,0.04)",
    color: colors.text.secondary,
    font: "inherit",
    fontSize: "0.78rem",
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    cursor: "pointer",
    transition: "background 0.15s ease-in-out, color 0.15s ease-in-out",
    "&:hover:not(:disabled)": {
        color: colors.text.primary,
        background: "rgba(255,255,255,0.07)",
    },
    "&:disabled": {
        opacity: 0.55,
        cursor: "not-allowed",
    },
    "&:focus, &:focus-visible": {
        outline: "none",
    },
};

export const ACCOUNT_PAGE_COUNTRY_SELECT_SX = {
    minWidth: { xs: "0 !important", sm: "0 !important" },
    maxWidth: { xs: "100%", sm: "100%" },
    width: "100%",
    flex: { xs: "1 1 auto", sm: "1 1 auto" },
    "& .MuiOutlinedInput-root": {
        borderRadius: { xs: "10px", sm: "10px" },
        minHeight: { xs: "0 !important", sm: "0 !important" },
        padding: { xs: "0 !important", sm: "0 !important" },
    },
    "& .MuiOutlinedInput-input, & .MuiAutocomplete-input": {
        fontSize: { xs: "0.82rem", sm: "0.82rem", xl: "0.92rem" },
        letterSpacing: "0.4px",
        marginLeft: 0,
        padding: "0.55rem 2rem 0.55rem 0.75rem !important",
    },
};

export const ACCOUNT_PAGE_MATCHMAKING_NOTICE_SX = {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.65rem",
    width: "100%",
    maxWidth: "36.75rem",
    mt: "0.7rem",
    px: "0.85rem",
    py: "0.75rem",
    borderRadius: "8px",
    border: `1px solid ${alpha(colors.primary.main, 0.26)}`,
    background: `
        linear-gradient(
            135deg,
            ${alpha(colors.primary.main, 0.11)} 0%,
            rgba(255,255,255,0.035) 100%
        )
    `,
    color: colors.text.secondary,
    fontSize: "0.78rem",
    fontWeight: 600,
    lineHeight: 1.45,
    letterSpacing: "0.01em",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
    "& svg": {
        mt: "0.05rem",
        flexShrink: 0,
        color: colors.primary.main,
        fontSize: "1.05rem",
    },
};

export const ACCOUNT_PAGE_INLINE_FIELDS_SX = {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: "0.75rem",
    width: "100%",
};

export const ACCOUNT_PAGE_INLINE_FIELD_SX = {
    display: "flex",
    flexDirection: "column",
    gap: "0.45rem",
    flex: "1 1 0",
    minWidth: 0,
};

export const ACCOUNT_PAGE_DELETION_SX = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
};

export const ACCOUNT_PAGE_DELETION_ROW_SX = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1.25rem",
    width: "100%",
};

export const ACCOUNT_PAGE_DELETION_CONTENT_SX = {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "0.35rem",
};

export const ACCOUNT_PAGE_DELETION_TITLE_SX = {
    color: colors.text.primary,
    fontSize: "0.95rem",
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: "0.01em",
};

export const ACCOUNT_PAGE_DELETION_COPY_SX = {
    color: colors.text.secondary,
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 1.55,
    letterSpacing: "0.01em",
};

export const ACCOUNT_PAGE_TWO_FACTOR_STATUS_SX = {
    fontSize: "0.8rem",
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
};

export const ACCOUNT_PAGE_DELETION_LINK_SX = {
    display: "inline-flex",
    mt: "0.85rem",
    color: colors.primary.main,
    fontSize: "0.875rem",
    fontWeight: 600,
    letterSpacing: "0.01em",
    textDecoration: "none",
    transition: "color 0.15s ease-in-out",
    "&:hover": {
        color: colors.accent.main,
        textDecoration: "none",
    },
};

export const ACCOUNT_PAGE_DELETION_BUTTON_SX = {
    flexShrink: 0,
    alignSelf: "center",
    border: "none",
    outline: "none",
    background: "transparent",
    color: colors.primary.main,
    font: "inherit",
    fontSize: "0.85rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textDecoration: "none",
    cursor: "pointer",
    padding: "0.4rem 0.65rem",
    borderRadius: "4px",
    transition: "background 0.15s ease-in-out, color 0.15s ease-in-out",
    "&:hover:not(:disabled)": {
        color: colors.primary.main,
        background: colors.accent.subtle,
        textDecoration: "none",
    },
    "&:disabled": {
        color: colors.text.muted,
        cursor: "not-allowed",
        background: "transparent",
    },
    "&:focus, &:focus-visible": {
        outline: "none",
    },
};

export const ACCOUNT_PAGE_DELETION_BUTTON_STACKED_SX = {
    ...ACCOUNT_PAGE_DELETION_BUTTON_SX,
    mt: "1.15rem",
    alignSelf: "flex-start",
    marginLeft: "-0.65rem",
};

export const ACCOUNT_PAGE_DELETION_STATUS_SX = {
    width: "100%",
    maxWidth: "36rem",
    mt: "1rem",
    px: "0.9rem",
    py: "0.8rem",
    borderRadius: "10px",
    border: `1px solid ${colors.border.default}`,
    background: "rgba(255,255,255,0.03)",
};

export const ACCOUNT_PAGE_DELETION_DIALOG_PAPER_SX = {
    background: `
        linear-gradient(
            180deg,
            rgba(17,17,20,0.98) 0%,
            rgba(10,10,12,0.99) 100%
        )
    `,
    border: `1px solid ${colors.border.default}`,
    borderRadius: "16px",
    boxShadow: "0 24px 64px rgba(0, 0, 0, 0.55)",
    color: colors.text.primary,
};

export const ACCOUNT_PAGE_DELETION_DIALOG_TITLE_SX = {
    color: colors.text.primary,
    fontSize: "1.15rem",
    fontWeight: 700,
    letterSpacing: "0.01em",
    px: "1.35rem",
    pt: "1.25rem",
    pb: "0.35rem",
};

export const ACCOUNT_PAGE_DELETION_DIALOG_CANCEL_SX = {
    color: colors.text.secondary,
    fontWeight: 600,
    textTransform: "none",
    "&:hover": {
        background: "rgba(255,255,255,0.06)",
        color: colors.text.primary,
    },
};

export const ACCOUNT_PAGE_FIELD_ERROR_SX = {
    color: colors.state.danger.main,
    fontSize: "0.75rem",
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: "0.01em",
    mt: "0.4rem",
};

export const ACCOUNT_PAGE_PHONE_VERIFIED_INPUT_SX = {
    "& .MuiOutlinedInput-root": {
        boxShadow: "none",
        "& fieldset": {
            borderColor: `${colors.state.success.main} !important`,
            borderWidth: "1px",
        },
        "&:hover": {
            boxShadow: "none",
            "& fieldset": {
                borderColor: `${colors.state.success.main} !important`,
            },
        },
        "&.Mui-focused": {
            boxShadow: "none",
            "& fieldset": {
                borderColor: `${colors.state.success.main} !important`,
                borderWidth: "1px",
            },
        },
        "&.Mui-readOnly, &.Mui-readOnly:hover, &.Mui-readOnly.Mui-focused": {
            boxShadow: "none",
            "& fieldset": {
                borderColor: `${colors.state.success.main} !important`,
                borderWidth: "1px",
            },
        },
    },
};

export const ACCOUNT_PAGE_KYC_VERIFIED_SX = {
    border: "1px solid rgba(34, 197, 94, 0.22)",
    background: `
        linear-gradient(
            165deg,
            rgba(10,18,14,0.96) 0%,
            rgba(10,12,12,0.98) 52%,
            rgba(10,10,12,0.98) 100%
        )
    `,
    "&::before": {
        background:
            "linear-gradient(90deg, transparent, rgba(34,197,94,0.4), transparent)",
    },
};

export const ACCOUNT_PAGE_KYC_STATUS_CHIP_SX = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.3rem",
    flexShrink: 0,
    px: "0.65rem",
    py: "0.28rem",
    borderRadius: "999px",
    background: "rgba(34, 197, 94, 0.16)",
    border: "1px solid rgba(34, 197, 94, 0.28)",
    color: colors.state.success.main,
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
};

export const ACCOUNT_PAGE_KYC_SUCCESS_PANEL_SX = {
    display: "flex",
    alignItems: "center",
    gap: "0.8rem",
    width: "100%",
    px: "0.9rem",
    py: "0.85rem",
    borderRadius: "10px",
    border: "1px solid rgba(34, 197, 94, 0.2)",
    background: "rgba(34, 197, 94, 0.06)",
};

export const ACCOUNT_PAGE_KYC_SX = {
    position: "relative",
    overflow: "hidden",
    width: "100%",
    mt: "0.35rem",
    px: "1.15rem",
    py: "1.2rem",
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: `
        linear-gradient(
            165deg,
            rgba(22,18,12,0.92) 0%,
            rgba(12,12,14,0.96) 48%,
            rgba(10,10,12,0.98) 100%
        )
    `,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "10%",
        right: "10%",
        height: "1px",
        background:
            "linear-gradient(90deg, transparent, rgba(246,185,76,0.45), transparent)",
        pointerEvents: "none",
    },
};

export const ACCOUNT_PAGE_KYC_BADGE_SX = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.35rem",
    flexShrink: 0,
    px: "0.6rem",
    py: "0.32rem",
    borderRadius: "999px",
    textDecoration: "none",
    color: colors.text.secondary,
    fontSize: "0.65rem",
    letterSpacing: "0.04em",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    transition: "border-color 0.15s ease-in-out, background 0.15s ease-in-out",
    "&:hover": {
        background: "rgba(255,255,255,0.07)",
        borderColor: "rgba(255,255,255,0.14)",
    },
};

/** Premium gold CTA — shared by identity verification, 2FA enable, etc. */
export const ACCOUNT_PAGE_PREMIUM_CTA_SX = {
    ...DIALOG_ACCEPT_BUTTON_SX,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    minHeight: "2.65rem",
    px: "1.35rem",
    py: "0.65rem",
    fontFamily: "inherit",
    fontSize: "0.82rem",
    fontWeight: 750,
    letterSpacing: "0.04em",
    textTransform: "none",
    whiteSpace: "nowrap",
    cursor: "pointer",
    "&:focus, &:focus-visible": {
        outline: "none",
    },
    "&:active:not(:disabled)": {
        transform: "translateY(1px)",
        filter: "brightness(0.98)",
    },
};

export const ACCOUNT_PAGE_KYC_CTA_SX = ACCOUNT_PAGE_PREMIUM_CTA_SX;

export const ACCOUNT_PAGE_SEND_CODE_BUTTON_DISABLED_SX = {
    opacity: 0.55,
    cursor: "not-allowed",
    pointerEvents: "none",
};

export const ACCOUNT_PAGE_SEND_CODE_BUTTON_SX = {
    ...ACCOUNT_PAGE_PREMIUM_CTA_SX,
    flexShrink: 0,
    ml: "auto",
    textTransform: "uppercase",
    fontSize: "0.78rem",
    letterSpacing: "0.06em",
    minHeight: "2.35rem",
};

export const ACCOUNT_PAGE_MOBILE_UNIFIED_SX = {
    ...ACCOUNT_PAGE_CARD_SX,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
};

export const ACCOUNT_PAGE_MOBILE_UNIFIED_DIVIDER_SX = {
    position: "relative",
    zIndex: 1,
    width: "100%",
    height: "1px",
    flexShrink: 0,
    background: "rgba(255,255,255,0.05)",
};

export const ACCOUNT_PAGE_MOBILE_EMBEDDED_MAIN_SX = {
    position: "relative",
    zIndex: 1,
    flex: 1,
    minWidth: 0,
    width: "100%",
    pt: "0.35rem",
};

export const ACCOUNT_PAGE_SPLIT_SX = {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: "1rem",
    width: "100%",
    minWidth: 0,
    minHeight: MAIN_SECTION_MIN_HEIGHT_SX,
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        flexDirection: "column",
        alignItems: "stretch",
    },
};

export const ACCOUNT_PAGE_SHELL_SX = {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    flex: 1,
    minHeight: MAIN_SECTION_MIN_HEIGHT_SX,
};

export const ACCOUNT_PAGE_MAIN_SX = {
    ...ACCOUNT_PAGE_CARD_SX,
    flex: 1,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    minHeight: MAIN_SECTION_MIN_HEIGHT_SX,
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        minHeight: 0,
    },
};

export const ACCOUNT_PAGE_SIDEBAR_STICKY_SX = {
    position: "sticky",
    top: SETTINGS_SIDEBAR_STICKY_TOP,
    alignSelf: "flex-start",
    flex: `0 0 ${SETTINGS_SIDEBAR_WIDTH_PX}px`,
    width: SETTINGS_SIDEBAR_WIDTH_PX,
    height: MAIN_SECTION_MIN_HEIGHT_SX,
    zIndex: 2,
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        position: "static",
        width: "100%",
        height: "auto",
        flex: "0 0 auto",
        alignSelf: "stretch",
    },
};

export const ACCOUNT_PAGE_SIDEBAR_SX = {
    ...ACCOUNT_PAGE_CARD_SX,
    minHeight: MAIN_SECTION_MIN_HEIGHT_SX,
    maxHeight: MAIN_SECTION_MIN_HEIGHT_SX,
    width: "100%",
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        minHeight: 0,
        maxHeight: "none",
    },
};

export const ACCOUNT_PAGE_SIDEBAR_HEADER_SX = {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    px: SETTINGS_SIDEBAR_PAD_X,
    pt: ACCOUNT_PAGE_CONTENT_PAD_Y,
    pb: "1.5rem",
    minWidth: 0,
    background: `
        linear-gradient(
            180deg,
            rgba(18,18,22,0.55) 0%,
            rgba(12,12,14,0) 100%
        )
    `,
    "&::before, &::after": {
        content: '""',
        position: "absolute",
        width: "0.85rem",
        height: "0.85rem",
        pointerEvents: "none",
        opacity: 0.9,
    },
    "&::before": {
        top: "0.7rem",
        left: "0.7rem",
        borderTop: `1px solid ${alpha(colors.primary.main, 0.45)}`,
        borderLeft: `1px solid ${alpha(colors.primary.main, 0.45)}`,
    },
    "&::after": {
        bottom: "0.7rem",
        right: "0.7rem",
        borderBottom: `1px solid ${alpha(colors.primary.main, 0.45)}`,
        borderRight: `1px solid ${alpha(colors.primary.main, 0.45)}`,
    },
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        px: SETTINGS_SIDEBAR_PAD_X_MOBILE,
        pt: "1.25rem",
        pb: "1.1rem",
        "&::before": {
            top: "0.55rem",
            left: "0.55rem",
        },
        "&::after": {
            bottom: "0.55rem",
            right: "0.55rem",
        },
    },
};

export const ACCOUNT_PAGE_SIDEBAR_DIVIDER_SX = {
    position: "relative",
    zIndex: 1,
    boxSizing: "border-box",
    width: `calc(100% - (${SETTINGS_SIDEBAR_NAV_INSET} * 2))`,
    height: "1px",
    mx: SETTINGS_SIDEBAR_NAV_INSET,
    mt: "0.15rem",
    mb: "0.55rem",
    background: "rgba(255,255,255,0.05)",
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        width: `calc(100% - (${SETTINGS_SIDEBAR_NAV_INSET_MOBILE} * 2))`,
        mx: SETTINGS_SIDEBAR_NAV_INSET_MOBILE,
    },
    "&::after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: "12%",
        right: "12%",
        height: "1px",
        background:
            "linear-gradient(90deg, transparent, rgba(255,190,90,0.18), transparent)",
        pointerEvents: "none",
    },
};

export const ACCOUNT_PAGE_SIDEBAR_NAV_SX = {
    ...ACCOUNT_PAGE_CARD_CONTENT_SX,
    flex: "0 0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "0.55rem",
    pt: "0.45rem",
    pb: "0.45rem",
    px: 0,
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        pt: "0.35rem",
        pb: "0.35rem",
        gap: "0.4rem",
    },
};

export const ACCOUNT_PAGE_SIDEBAR_SPACER_SX = {
    flex: 1,
    minHeight: "1.25rem",
};

export const ACCOUNT_PAGE_SIDEBAR_LOGOUT_HAIRLINE_SX = {
    position: "relative",
    zIndex: 1,
    mx: SETTINGS_SIDEBAR_NAV_INSET,
    mb: "8px",
    height: "1px",
    background:
        "linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent)",
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        mx: SETTINGS_SIDEBAR_NAV_INSET_MOBILE,
    },
};

export const ACCOUNT_PAGE_SIDEBAR_LOGOUT_SX = {
    position: "relative",
    zIndex: 1,
    flexShrink: 0,
    pb: "18px",
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        pb: "14px",
    },
};
